// client/src/modals/SimulationReport.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CourseDomains } from '../../types/courseDomains';

import './simulationReport.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (date) =>
  date
    ? new Date(date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

const scoreColor = (score, max = 10) => {
  if (score == null) return '#6b7280';
  const pct = score / max;
  if (pct >= 0.8) return '#10b981';
  if (pct >= 0.6) return '#f59e0b';
  return '#ef4444';
};

const scoreLabelClass = (score) => {
  if (score == null) return '';
  if (score >= 9)   return 'grading-score-label--excellent';
  if (score >= 8)   return 'grading-score-label--great';
  if (score >= 6.5) return 'grading-score-label--good';
  if (score >= 5)   return 'grading-score-label--fair';
  return 'grading-score-label--needs-work';
};

const scoreLabelText = (score) => {
  if (score == null) return '—';
  if (score >= 9)   return 'Excellent';
  if (score >= 8)   return 'Great';
  if (score >= 6.5) return 'Good';
  if (score >= 5)   return 'Fair';
  return 'Needs Work';
};

const confidenceClass = (c) => ({
  high:   'grading-badge--confidence-high',
  medium: 'grading-badge--confidence-medium',
  low:    'grading-badge--confidence-low',
}[c?.toLowerCase()] || '');

const typeInfo = (type) => ({
  error:   { mod: 'error',   icon: '❌' },
  warning: { mod: 'warning', icon: '⚠️' },
  info:    { mod: 'info',    icon: 'ℹ️' },
}[type?.toLowerCase()] || { mod: 'info', icon: '•' });

const tierLabel = (tier) => ({
  full_100:   { label: 'Full marks', cls: 'grading-tier--full'    },
  partial_75: { label: '75%',        cls: 'grading-tier--partial' },
  partial_50: { label: '50%',        cls: 'grading-tier--partial' },
  partial_25: { label: '25%',        cls: 'grading-tier--partial' },
  fail:       { label: 'Not met',    cls: 'grading-tier--fail'    },
}[tier] || (tier ? { label: tier, cls: 'grading-tier--partial' } : null));

// ADD after the tierLabel helper (around line 59):
const formatReqKey = (key) => {
  // req_1 → Requirement 1
  // req_10 → Requirement 10
  // R1 → Requirement 1
  // requirement_2 → Requirement 2
  // anything else → title-cased as-is
  const match = key.match(/(\d+)$/);
  if (match) {
    return `Requirement ${match[1]}`;
  }
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Extract the display text from a serial log entry.
// DB records may use different field names than in-memory state objects.
const extractSerialMessage = (item) => {
  if (typeof item === 'string') return item;
  // Try known field names in priority order
  return item.message
      || item.input
      || item.output
      || item.data
      || item.text
      || item.content
      || (typeof item === 'object' ? JSON.stringify(item) : String(item));
};

// ─── Score ring ───────────────────────────────────────────────────────────────

const ScoreRing = ({ score, max = 10, size = 120 }) => {
  const r     = size / 2 - 10;
  const circ  = 2 * Math.PI * r;
  const pct   = score != null ? Math.min(score / max, 1) : 0;
  const dash  = pct * circ;
  const color = scoreColor(score, max);
  const cx    = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="grading-ring">
      <circle className="grading-ring__track"    cx={cx} cy={cx} r={r} />
      <circle className="grading-ring__progress" cx={cx} cy={cx} r={r}
        stroke={color} strokeDasharray={`${dash} ${circ}`}
        transform={`rotate(-90 ${cx} ${cx})`} />
      <text className="grading-ring__score"
        x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize={size * 0.22}>
        {score != null ? score.toFixed(1) : '—'}
      </text>
      <text className="grading-ring__max"
        x="50%" y="66%" textAnchor="middle" dominantBaseline="middle"
        fontSize={size * 0.1}>
        / {max}
      </text>
    </svg>
  );
};

// ─── Collapsible section ──────────────────────────────────────────────────────

const GradingSection = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="grading-section">
      <button className="grading-section-toggle" onClick={() => setOpen(o => !o)}>
        <span>{icon} {title}</span>
        <span className="grading-section-toggle-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="grading-section-body">{children}</div>}
    </div>
  );
};

// ─── Requirement card ─────────────────────────────────────────────────────────

const RequirementCard = ({ reqKey, req }) => {
  const [open, setOpen] = useState(false);
  const color  = scoreColor(req.score, req.max || 10);
  const pct    = req.max ? Math.round((req.score / req.max) * 100) : (req.percentage ?? 0);
  const tier   = tierLabel(req.tierMatched);
  const passed = req.passed;
  return (
    <div className={`grading-req-card ${passed ? 'grading-req-card--pass' : 'grading-req-card--fail'}`}>
      <button className="grading-req-header" onClick={() => setOpen(o => !o)}>
        <span className={`grading-req-status-dot ${passed ? 'grading-req-status-dot--pass' : 'grading-req-status-dot--fail'}`} />
        <span className="grading-req-key">{reqKey}</span>
        <span className="grading-req-desc">{req.description || '—'}</span>
        <div className="grading-req-right">
          {tier && <span className={`grading-req-tier ${tier.cls}`}>{tier.label}</span>}
          <span className="grading-req-score" style={{ color }}>
            {req.score ?? '—'}<span className="grading-req-score-max">/{req.max ?? 10}</span>
          </span>
          <span className="grading-req-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </button>
      <div className="grading-req-bar-track">
        <div className="grading-req-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {open && req.feedback && (
        <div className="grading-req-feedback">
          <span className="grading-req-feedback-label">AI Feedback</span>
          <p className="grading-req-feedback-text">{req.feedback}</p>
        </div>
      )}
    </div>
  );
};

// ─── Stat cell ────────────────────────────────────────────────────────────────

const StatCell = ({ label, value }) => (
  <div className="grading-stat-cell">
    <div className="grading-stat-cell-label">{label}</div>
    <div className="grading-stat-cell-value">{value}</div>
  </div>
);

// ─── Snapshot card ────────────────────────────────────────────────────────────

// const SnapshotCard = ({ snapshot, index }) => {
//   const [open, setOpen]  = useState(false);
//   const sensorKeys   = snapshot.sensors        ? Object.keys(snapshot.sensors)        : [];
//   const actuatorKeys = snapshot.actuatorStates ? Object.keys(snapshot.actuatorStates) : [];
//   return (
//     <div className="grading-snapshot-card">
//       <button className="grading-snapshot-toggle" onClick={() => setOpen(o => !o)}>
//         <span className="grading-snapshot-index">#{String(index + 1).padStart(2, '0')}</span>
//         <span className="grading-snapshot-time">{fmt(snapshot.timestamp)}</span>
//         <span className="grading-snapshot-counts">
//           {sensorKeys.length}S · {actuatorKeys.length}A {open ? '▲' : '▼'}
//         </span>
//       </button>
//       {open && (
//         <div className="grading-snapshot-body">
//           {sensorKeys.length > 0 && (
//             <div className="grading-snapshot-sensors">
//               <div className="grading-snapshot-section-label">Sensors</div>
//               {sensorKeys.map(k => (
//                 <div key={k} className="grading-snapshot-row">
//                   <span className="grading-snapshot-row-key">{k}</span>
//                   <span className="grading-snapshot-row-val">
//                     {typeof snapshot.sensors[k] === 'object'
//                       ? JSON.stringify(snapshot.sensors[k])
//                       : snapshot.sensors[k]}
//                   </span>
//                 </div>
//               ))}
//             </div>
//           )}
//           {actuatorKeys.length > 0 && (
//             <div>
//               <div className="grading-snapshot-section-label">Actuators</div>
//               {actuatorKeys.map(k => (
//                 <div key={k} className="grading-snapshot-actuator-entry">
//                   <span className="grading-snapshot-actuator-key">{k}: </span>
//                   {JSON.stringify(snapshot.actuatorStates[k])}
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };
const SnapshotCard = ({ snapshot, index, domain = 'IoT' }) => {
  const [open, setOpen] = useState(false);

  // ✅ IoT: reads sensors/actuatorStates
  // ✅ IIoT: reads sensorText/actuatorStates/plcToGateway/gatewayToAzure/azureDecoded
  const isIIoT = domain === 'IIoT';

  const sensorKeys   = isIIoT
    ? Object.keys(snapshot.sensorText     || {})
    : Object.keys(snapshot.sensors        || {});
  const actuatorKeys = Object.keys(snapshot.actuatorStates || {});

  const sensorData   = isIIoT ? snapshot.sensorText : snapshot.sensors;

  return (
    <div className="grading-snapshot-card">
      <button className="grading-snapshot-toggle" onClick={() => setOpen(o => !o)}>
        <span className="grading-snapshot-index">#{String(index + 1).padStart(2, '0')}</span>
        <span className="grading-snapshot-time">{fmt(snapshot.timestamp)}</span>
        <span className="grading-snapshot-counts">
          {sensorKeys.length}S · {actuatorKeys.length}A {open ? '▲' : '▼'}
        </span>
      </button>
      {open && (
        <div className="grading-snapshot-body">
          {/* Sensors / sensorText */}
          {sensorKeys.length > 0 && (
            <div className="grading-snapshot-sensors" >
              <div className="grading-snapshot-section-label">
                {isIIoT ? 'Sensor Values (PLC)' : 'Sensors'}
              </div>
              {sensorKeys.map(k => (
                <div key={k} className="grading-snapshot-row">
                  <span className="grading-snapshot-row-key">{k}</span>
                  <span className="grading-snapshot-row-val">
                    {typeof sensorData[k] === 'object'
                      ? JSON.stringify(sensorData[k])
                      : sensorData[k]}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Actuator states */}
          {actuatorKeys.length > 0 && (
            <div>
              <div className="grading-snapshot-section-label">Actuators</div>
              {actuatorKeys.map(k => (
                <div key={k} className="grading-snapshot-actuator-entry">
                  <span className="grading-snapshot-actuator-key">{k}: </span>
                  {JSON.stringify(snapshot.actuatorStates[k])}
                </div>
              ))}
            </div>
          )}
          {/* IIoT-only: protocol pipeline */}
          {isIIoT && snapshot.plcToGateway?.formatted && (
            <div className="grading-snapshot-sensors" style={{ marginTop: '8px' }}>
              <div className="grading-snapshot-section-label">PLC → Gateway</div>
              <div className="grading-snapshot-row">
                <span className="grading-snapshot-row-key">{snapshot.plcToGateway.protocol}</span>
                {/* <span className="grading-snapshot-row-val" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                  {snapshot.plcToGateway.formatted}
                </span> */}
  {snapshot.plcToGateway.formatted
  .split('|')
  .map((item, i) => (
    <span
      key={i}
      className="grading-snapshot-row-valg"
      style={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }}
    >
      {item.trim()}
    </span>
  ))}
              </div>
            </div>
          )}
          {isIIoT && snapshot.gatewayToAzure?.formatted && (
            <div className="grading-snapshot-sensors" style={{ marginTop: '8px' }}>
              <div className="grading-snapshot-section-label">Gateway → Azure</div>
              <div className="grading-snapshot-row">
                <span className="grading-snapshot-row-key">{snapshot.gatewayToAzure.protocol}</span>
                {/* <span className="grading-snapshot-row-val" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                  {snapshot.gatewayToAzure.formatted}
                </span> */}
  {snapshot.gatewayToAzure.formatted
  .split(',')
  .map((item, i) => (
    <span
      key={i}
      className="grading-snapshot-row-valg"
      style={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }}
    >
      {item.trim()}
    </span>
  ))}
              </div>
            </div>
          )}
          {isIIoT && Object.keys(snapshot.azureDecoded || {}).length > 0 && (
            <div className="grading-snapshot-sensors" style={{ marginTop: '8px' }}>
              <div className="grading-snapshot-section-label">Azure Decoded</div>
              {Object.entries(snapshot.azureDecoded).map(([k, v]) => (
                <div key={k} className="grading-snapshot-row">
                  <span className="grading-snapshot-row-key">{k}</span>
                  <span className="grading-snapshot-row-val">{String(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
// ─── Validation list ──────────────────────────────────────────────────────────

const ValidationList = ({ items, emptyIcon, emptyText }) => {
  if (!items || items.length === 0) {
    return (
      <div className="grading-validation-empty">
        <div className="grading-validation-empty-icon">{emptyIcon}</div>
        <p className="grading-validation-empty-text">{emptyText}</p>
      </div>
    );
  }
  const counts = items.reduce((acc, item) => {
    const t = item.type?.toLowerCase() || 'info';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  return (
    <div>
      <div className="grading-validation-summary">
        {['error', 'warning', 'info'].filter(t => counts[t]).map(t => {
          const { icon } = typeInfo(t);
          return (
            <span key={t} className={`grading-validation-pill grading-validation-pill--${t}`}>
              {icon} {counts[t]} {t}{counts[t] > 1 ? 's' : ''}
            </span>
          );
        })}
      </div>
      <div className="grading-validation-list">
        {items.map((item, i) => {
          const { mod, icon } = typeInfo(item.type);
          return (
            <div key={item._id || i} className={`grading-validation-item grading-validation-item--${mod}`}>
              <span className="grading-validation-item-icon">{icon}</span>
              <div className="grading-validation-item-body">
                <div className="grading-validation-item-message">{item.message}</div>
                {item.component && (
                  <div className="grading-validation-item-component">🔌 {item.component}</div>
                )}
              </div>
              <span className={`grading-validation-item-type grading-validation-item-type--${mod}`}>
                {item.type || 'info'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Pending spinner ──────────────────────────────────────────────────────────

const GradingPending = () => (
  <div className="grading-pending-state">
    <div className="grading-pending-spinner" />
    <div className="grading-pending-title">🤖 Prof-iX is grading your submission…</div>
    <p className="grading-pending-desc">This usually takes a few seconds. Please wait.</p>
  </div>
);

// ─── Main modal ───────────────────────────────────────────────────────────────

const SimulationReport = ({
  visible,
  simulator,
  circuit,
  onClose,
  onSubmit,
  onRefreshSimulator,
  readOnly = false,   // true in student page — no submit/poll, just view results
  domain = 'IoT',
}) => {
  const overlayRef = useRef(null);
  const pollRef    = useRef(null);

  const [activeTab,    setActiveTab]    = useState('overview');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling,    setIsPolling]    = useState(false);

  // localPayload: stable copy of gradingPayload — only ever upgrades, never clears.
  // We use a ref AND state so that the stopPolling check always reads the latest
  // value without needing it in the dependency array.
  const localPayloadRef = useRef(null);
  const [localPayload, _setLocalPayload] = useState(null);

  const setLocalPayload = useCallback((val) => {
    localPayloadRef.current = val;
    _setLocalPayload(val);
  }, []);

  // ── Derived status — must be above all useEffects that reference these ──
  const payload       = localPayload;
  const result        = payload?.gradingResult;        
  const snapshots     = simulator?.simulationSnapshots || [];  
  const gradingStatus = payload?.status;
  const isPending     = gradingStatus === 'pending';
  const isCompleted   = gradingStatus === 'completed';
  const isGraded      = gradingStatus === 'graded';
  const alreadyGraded = !!payload;
  const waitingResult = isPending;

  useEffect(() => {
    const incoming = simulator?.gradingPayload;
    console.log('📦 useEffect — incoming:', incoming?.status, '| current:', localPayloadRef.current?.status);
    if (!incoming) return;
    const current = localPayloadRef.current;
    if (!current) {
      setLocalPayload(incoming);
    } else if (incoming.status !== current.status) {
      // upgrade whenever status changes: pending→completed, completed→graded
      setLocalPayload(incoming);
    }
  }, [simulator?.gradingPayload?.status, setLocalPayload]);

  // Reset everything when modal opens with no prior payload (submit mode only)
  useEffect(() => {
    if (readOnly) return; // readOnly: payload already there, nothing to reset
    if (visible && !simulator?.gradingPayload && !localPayloadRef.current) {
      setLocalPayload(null);
      setIsPolling(false);
      setIsSubmitting(false);
    }
  }, [visible, readOnly, simulator?.gradingPayload, setLocalPayload]);

  // ── Close handlers ────────────────────────────────────────────────────────
  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose(); };

  useEffect(() => {
    if (!visible) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, onClose]);

  useEffect(() => {
    if (!visible) stopPolling();
    return () => stopPolling();
  }, [visible]);

  // ── Polling (submit mode only) ────────────────────────────────────────────
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setIsPolling(false);
  };

  const startPolling = useCallback(() => {
    if (readOnly || !onRefreshSimulator || pollRef.current) return;
    setIsPolling(true);
    pollRef.current = setInterval(async () => {
      await onRefreshSimulator();
      if (localPayloadRef.current?.status === 'completed' || 
          localPayloadRef.current?.status === 'graded') {
        stopPolling();
      }
    }, 3000);
  }, [readOnly, onRefreshSimulator]);

  // Also stop if localPayload state update brings gradingResult
  useEffect(() => {
    if (isPolling && (localPayload?.status === 'completed' || 
                      localPayload?.status === 'graded')) {
      stopPolling();
    }
  }, [localPayload?.status, isPolling]);

  useEffect(() => {
    if (!visible || readOnly) return;
    if (isPending && !pollRef.current) {
      startPolling();
    }
  }, [visible, isPending, readOnly, startPolling]);

  // ── Submit (submit mode only) ─────────────────────────────────────────────
  const handleSubmit = async () => {
    if (readOnly || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit();  // → submitGrades → sets status='graded', submittedAt
      // onRefreshSimulator will update localPayload via the useEffect upgrade logic
      await onRefreshSimulator?.();
    } catch (err) {
      console.error('Submit grades error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!visible) return null;

  // show spinner iff status=pending
  const codeValidationResults    = simulator?.codeValidationResults    || [];
  const circuitValidationResults = circuit?.circuitValidationResults   || [];
  // console.log('🔌 SimulationReport circuit prop:', { name: circuit?.name, circuitValidationResults: circuit?.circuitValidationResults });
  
  const breakdownEntries = result?.breakdown
    ? Object.entries(result.breakdown).filter(([, v]) => v != null)
    : [];

  // Build combined serial log — handle varying DB field names for message text
  const rawSerialOut = simulator?.serialOutput || [];
  const rawSerialIn  = simulator?.serialInput  || [];
  const combinedSerial = [
    ...rawSerialOut.map(o => ({ timestamp: o.timestamp, text: extractSerialMessage(o), dir: 'out', type: o.type })),
    ...rawSerialIn .map(i => ({ timestamp: i.timestamp, text: extractSerialMessage(i), dir: 'in',  type: i.type })),
  ].filter(item => item.timestamp)
   .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Tab badges
  const codeErrCount     = codeValidationResults.filter(r => r.type?.toLowerCase() === 'error').length;
  const codeWarnCount    = codeValidationResults.filter(r => r.type?.toLowerCase() === 'warning').length;
  const circuitErrCount  = circuitValidationResults.filter(r => r.type?.toLowerCase() === 'error').length;
  const circuitWarnCount = circuitValidationResults.filter(r => r.type?.toLowerCase() === 'warning').length;
  const codeBadge        = codeErrCount    ? ` ❌${codeErrCount}`    : codeWarnCount    ? ` ⚠️${codeWarnCount}`    : codeValidationResults.length    ? ' ✅' : '';
  const circuitBadge     = circuitErrCount ? ` ❌${circuitErrCount}` : circuitWarnCount ? ` ⚠️${circuitWarnCount}` : circuitValidationResults.length ? ' ✅' : '';

  const domainConfig    = CourseDomains[domain] || CourseDomains.IoT;
  const reportTabs      = domainConfig.reportTabs || [];

  const evidenceData = {};
  if (domainConfig.gradingPayloadFields) {
    Object.keys(domainConfig.gradingPayloadFields).forEach(key => {
      evidenceData[key] = result?.[key] ?? domainConfig.gradingPayloadFields[key];
    });
  }

  // Specific shorthands used in tab rendering:
  const notebookCells = simulator?.notebookCells || [];
  const cellOutputs   = evidenceData.cellOutputs  || [];
  const nrFlowNodes   = evidenceData.flowNodes    || [];
  const nrFlowWires   = evidenceData.flowWires    || [];
  const nrGatewayConfig = evidenceData.gatewayConfig || {};
  const nrFlowStats   = evidenceData.flowStats    || {};


  const passedCount = breakdownEntries.filter(([, v]) => v?.passed).length;
  const totalCount  = breakdownEntries.length;
  const totalMax    = breakdownEntries.reduce((s, [, v]) => s + (v?.max ?? 0), 0) || 10;

  const tabs = (CourseDomains[domain]?.reportTabs || CourseDomains.IoT.reportTabs).map(tab => ({
      ...tab,
      label: tab.id === 'snapshots'        ? `📸 Snapshots (${snapshots.length})`          :
            tab.id === 'notebook'         ? `📓 Notebook (${notebookCells.length} cells)`  :
            tab.id === 'code-validation'  ? `🔍 ${tab.label.replace('🔍 ', '')}${codeBadge}`   :
            tab.id === 'circuit-validation' ? `🔌 ${tab.label.replace('🔌 ', '')}${circuitBadge}` :
            tab.label
  }));

  return (
    <div className="grading-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="grading-modal">

        {/* ── Header ── */}
        <div className="grading-header">
          <div className="grading-header-left">
            <span className="grading-header-icon">{readOnly ? '📊' : '📤'}</span>
            <div>
              <div className="grading-header-title">
                {readOnly
                    ? CourseDomains[domain]?.reportTitle || '📡 Lab Report'
                    : CourseDomains[domain]?.gradingTitle || '📡 Lab Grading'
                }
              </div>
              <div className="grading-header-sub">{circuit?.name}</div>
            </div>
          </div>
          <button className="grading-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* ── Status bar ── */}
        <div className="grading-status-bar">
          {isPending && (
            <>
              <span className="grading-badge grading-badge--pending">⏳ Prof-iX is grading…</span>
              <span className="grading-meta-text">Submitted for grading</span>
            </>
          )}
          {isCompleted && (
            <>
              <span className="grading-badge grading-badge--completed">📊 Grading Complete</span>
              {result?.model    && <span className="grading-meta-text">Model: {result.model}</span>}
              {payload?.gradedAt && <span className="grading-meta-text">Graded {fmt(payload.gradedAt)}</span>}
              <span className="grading-meta-text grading-meta-text--hint">Review your score below, then Submit Grades</span>
            </>
          )}
          {isGraded && (
            <>
              <span className="grading-badge grading-badge--success">✅ Grades Submitted</span>
              <span className="grading-meta-text">Submitted {fmt(payload.submittedAt)}</span>
              {result?.model && <span className="grading-meta-text">Model: {result.model}</span>}
            </>
          )}
          {!alreadyGraded && (
            <span className="grading-badge grading-badge--pending">⏳ Not yet submitted</span>
          )}
        </div>

        {/* ── Tab bar ── */}
        <div className="grading-tab-bar">
          {tabs.map(t => (
            <button key={t.id}
              className={`grading-tab${activeTab === t.id ? ' grading-tab--active' : ''}`}
              onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div className="grading-body">

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div>
              {waitingResult ? (
                <GradingPending />
              ) : result ? (
                <>
                  <div className="grading-score-row">
                    <ScoreRing score={result.score} max={totalMax} />
                    <div style={{ flex: 1 }}>
                      <div className={`grading-score-label ${scoreLabelClass(result.score)}`}>
                        {scoreLabelText(result.score)}
                      </div>
                      <div className="grading-score-reasoning">{result.reasoning}</div>
                      {result.confidence && (
                        <div className="grading-score-confidence-row">
                          <span className="grading-score-confidence-label">Confidence</span>
                          <span className={`grading-badge ${confidenceClass(result.confidence)}`}>
                            {result.confidence}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <GradingSection title="Feedback" icon="💬">
                    <p className="grading-feedback-text">{result.feedback}</p>
                  </GradingSection>

                  {breakdownEntries.length > 0 && (
                    <GradingSection title={`Requirements — ${passedCount}/${totalCount} passed`} icon="📐">
                      <div className="grading-req-list">
                        {/* {breakdownEntries.map(([key, val]) => (
                          <RequirementCard key={key} reqKey={key} req={val} />
                        ))} */}
                        {breakdownEntries.map(([key, val]) => (
                          <RequirementCard
                            key={key}
                            reqKey={formatReqKey(key)}
                            req={val}
                          />
                        ))}
                      </div>
                    </GradingSection>
                  )}

                  <GradingSection title="Session Stats" icon="📈">
                    <div className="grading-stats-grid">
                      <StatCell label="Snapshots"  value={snapshots.length} />
                      <StatCell label="Duration"   value={`${payload?.duration || 0}s`} />
                      <StatCell label="Submitted"  value={fmt(payload?.submittedAt)} />
                    </div>
                  </GradingSection>
                </>
              ) : (
                <div className="grading-empty-state">
                  <div className="grading-empty-icon">📤</div>
                  <div className="grading-empty-title">Ready to submit for grading</div>
                  <p className="grading-empty-desc">
                    Run your simulation so sensor readings and actuator states are captured,
                    then press <strong>Submit for Grading</strong> below.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SNAPSHOTS */}
          {activeTab === 'snapshots' && (
            <div>
              {snapshots.length > 0
                ? snapshots.map((s, i) => (
                    <SnapshotCard key={i} snapshot={s} index={i} domain={domain} />  // ✅ pass domain
                  ))
                : <div className="grading-empty-state">
                    <div className="grading-empty-icon">📸</div>
                    <p className="grading-empty-desc">No snapshots captured yet.</p>
                  </div>
              }
            </div>
          )}

          {/* SERIAL — combined output + input, handles varying DB field names */}
          {activeTab === 'serial' && (
            <div>
              {combinedSerial.length > 0 ? (
                <div className="grading-code-block">
                  {combinedSerial.map((item, i) => (
                    <div key={i} className="grading-serial-line">
                      <span className="grading-serial-timestamp">
                        [{new Date(item.timestamp).toLocaleTimeString()}]
                      </span>{' '}
                      <span className={item.dir === 'in' ? 'grading-serial-msg--in' : 'grading-serial-msg--out'}>
                        {item.dir === 'in' ? '▶ ' : '  '}{item.text}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grading-empty-state">
                  <div className="grading-empty-icon">🖥️</div>
                  <p className="grading-empty-desc">No serial data recorded.</p>
                </div>
              )}
            </div>
          )}

          {/* CODE */}
          {activeTab === 'code' && (
            <div>
              {simulator?.uploadedCode ? (
                <div className="grading-code-block">
                  <pre className="grading-code-pre">{simulator.uploadedCode}</pre>
                </div>
              ) : (
                <div className="grading-empty-state">
                  <div className="grading-empty-icon">💻</div>
                  <p className="grading-empty-desc">No code uploaded yet.</p>
                </div>
              )}
            </div>
          )}

          {/* CODE VALIDATION */}
          {activeTab === 'code-validation' && (
            <ValidationList items={codeValidationResults}
              emptyIcon="✅" emptyText="No code validation issues found." />
          )}

          {/* CIRCUIT VALIDATION */}
          {activeTab === 'circuit-validation' && (
            <ValidationList items={circuitValidationResults}
              emptyIcon="✅" emptyText="No circuit validation issues found." />
          )}

          {activeTab === 'notebook' && reportTabs.some(t => t.id === 'notebook') && (
            <div>
              {notebookCells.length > 0 ? (
                notebookCells.map((cell, i) => {
                  const output = cellOutputs.find(o => o.cellId === cell.cellId)
                    || (cell.simOutputs?.stdout ? {
                        executed: true,
                        stdout:   cell.simOutputs.stdout,
                        error:    cell.simOutputs.error || '',
                      } : null);
                  return (
                    <div key={cell.cellId} style={{
                      marginBottom: '12px', border: '1px solid #e2e8f0',
                      borderRadius: '8px', overflow: 'hidden',
                    }}>
                      <div style={{
                        padding: '6px 12px', background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        fontSize: '12px', color: '#64748b',
                        display: 'flex', justifyContent: 'space-between',
                      }}>
                        <span>Cell {i + 1} — {cell.componentId || 'custom'}</span>
                        <span>{output?.executed ? '✅ Executed' : '⬜ Not run'}</span>
                      </div>
                      <pre style={{
                        background: '#1e1e2e', color: '#cdd6f4',
                        padding: '10px 14px', margin: 0,
                        fontSize: '12px', fontFamily: 'monospace',
                        overflowX: 'auto', maxHeight: '200px',
                      }}>
                        {cell.code}
                      </pre>
                      {output?.stdout && (
                        <pre style={{
                          background: '#f8fafc', color: '#1e293b',
                          padding: '8px 14px', margin: 0,
                          fontSize: '12px', fontFamily: 'monospace',
                          borderTop: '1px solid #e2e8f0',
                        }}>
                          {output.stdout}
                        </pre>
                      )}
                      {output?.error && (
                        <pre style={{
                          background: '#fff5f5', color: '#dc2626',
                          padding: '8px 14px', margin: 0,
                          fontSize: '12px', fontFamily: 'monospace',
                          borderTop: '1px solid #fee2e2',
                        }}>
                          {output.error}
                        </pre>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="grading-empty-state">
                  <div className="grading-empty-icon">📓</div>
                  <p className="grading-empty-desc">No notebook cells recorded.</p>
                </div>
              )}
            </div>
          )}

          {/* DASHBOARD — IIoT Node-RED flow summary */}
          {activeTab === 'dashboard' && reportTabs.some(t => t.id === 'dashboard') && (
            <div>
              {/* ── Flow stats strip ──────────────────────────────────────────── */}
              <div style={{
                display: 'flex', gap: '10px', flexWrap: 'wrap',
                marginBottom: '16px'
              }}>
                {[
                  { label: 'Total Nodes',  value: nrFlowStats.totalNodes      ?? nrFlowNodes.length },
                  { label: 'Enabled',      value: nrFlowStats.enabledNodes     ?? '—' },
                  { label: 'Input',        value: nrFlowStats.inputNodes       ?? '—' },
                  { label: 'Processing',   value: nrFlowStats.processingNodes  ?? '—' },
                  { label: 'Output',       value: nrFlowStats.outputNodes      ?? '—' },
                  { label: 'Wires',        value: nrFlowStats.totalWires       ?? nrFlowWires.length },
                  { label: 'Complete Path',value: nrFlowStats.hasCompletePath  ? '✓ Yes' : '✗ No' },
                ].map(s => (
                  <div key={s.label} style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: '8px', padding: '8px 14px',
                    textAlign: 'center', minWidth: '80px', flex: '1'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                      {s.value}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* ── Gateway protocols ─────────────────────────────────────────── */}
              {Object.keys(nrGatewayConfig).length > 0 && (
                <GradingSection title="Gateway Protocol Configuration" icon="🔴">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {Object.entries(nrGatewayConfig).map(([protocol, cfg]) => (
                      <div key={protocol} style={{
                        flex: '1', minWidth: '180px',
                        background: cfg.enabled ? '#f0fdf4' : '#f8fafc',
                        border: `1px solid ${cfg.enabled ? '#bbf7d0' : '#e2e8f0'}`,
                        borderRadius: '8px', padding: '10px 14px'
                      }}>
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          marginBottom: '8px', fontSize: '12px', fontWeight: 700
                        }}>
                          <span>{protocol.toUpperCase()}</span>
                          <span style={{ color: cfg.enabled ? '#16a34a' : '#94a3b8' }}>
                            {cfg.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        {cfg.enabled && cfg.settings && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {Object.entries(cfg.settings)
                              .filter(([, v]) => v !== '' && v !== null && v !== undefined)
                              .slice(0, 5)
                              .map(([k, v]) => (
                                <div key={k} style={{
                                  display: 'flex', justifyContent: 'space-between',
                                  fontSize: '11px', padding: '2px 6px',
                                  background: '#fff', borderRadius: '3px'
                                }}>
                                  <span style={{ color: '#64748b' }}>
                                    {k.replace(/([A-Z])/g, ' $1').trim()}
                                  </span>
                                  <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                    {typeof v === 'boolean' ? (v ? '✓' : '✗') : String(v)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </GradingSection>
              )}

              {/* ── Flow nodes ────────────────────────────────────────────────── */}
              {nrFlowNodes.length > 0 ? (
                ['nr_input', 'nr_processing', 'nr_output'].map(cat => {
                  const catNodes = nrFlowNodes.filter(n => n.category === cat);
                  if (catNodes.length === 0) return null;
                  const catColors = {
                    nr_input:      '#3FADB5',
                    nr_processing: '#FAB040',
                    nr_output:     '#87A980',
                  };
                  const catLabels = {
                    nr_input:      '📥 Input Nodes',
                    nr_processing: '⚙️ Processing Nodes',
                    nr_output:     '📤 Output Nodes',
                  };
                  return (
                    <GradingSection
                      key={cat}
                      title={`${catLabels[cat]} (${catNodes.length})`}
                      icon=""
                      defaultOpen={false}
                    >
                      {catNodes.map((node, i) => (
                        <div key={node.instanceId || i} style={{
                          border: `1px solid #e2e8f0`,
                          borderLeft: `4px solid ${catColors[cat]}`,
                          borderRadius: '6px',
                          padding: '10px 14px',
                          marginBottom: '8px',
                          background: node.enabled ? '#fff' : '#f8fafc',
                          opacity: node.enabled ? 1 : 0.6,
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center',
                            gap: '8px', marginBottom: '6px'
                          }}>
                            <span style={{
                              padding: '2px 6px', borderRadius: '4px',
                              background: catColors[cat], color: '#fff',
                              fontSize: '10px', fontFamily: 'monospace', fontWeight: 700
                            }}>
                              {node.nodeRedType}
                            </span>
                            <span style={{ fontWeight: 600, fontSize: '13px' }}>
                              {node.label}
                            </span>
                            <span style={{
                              fontSize: '11px', marginLeft: 'auto',
                              color: node.enabled ? '#10b981' : '#94a3b8'
                            }}>
                              {node.enabled ? '● Active' : '○ Disabled'}
                            </span>
                          </div>

                          {/* Config summary */}
                          {node.configSummary && Object.values(node.configSummary)
                              .some(cfg => cfg.enabled) && (
                            <div style={{
                              display: 'flex', flexWrap: 'wrap', gap: '4px',
                              fontSize: '11px'
                            }}>
                              {Object.entries(node.configSummary)
                                .filter(([, cfg]) => cfg.enabled)
                                .flatMap(([protocol, cfg]) =>
                                  Object.entries(cfg.settings || {})
                                    .filter(([, v]) => v !== '' && v !== null)
                                    .slice(0, 3)
                                    .map(([k, v]) => (
                                      <span key={`${protocol}-${k}`} style={{
                                        padding: '1px 6px', background: '#f1f5f9',
                                        borderRadius: '3px', color: '#475569'
                                      }}>
                                        {k}: <strong>{String(v)}</strong>
                                      </span>
                                    ))
                                )}
                            </div>
                          )}

                          {/* Active ports */}
                          {(node.activePins || []).length > 0 && (
                            <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {node.activePins.map(pin => (
                                <span key={pin.pinName} style={{
                                  fontSize: '10px', padding: '1px 6px',
                                  background: pin.direction === 'input' ? '#e0f7fa' : '#fff8e1',
                                  borderRadius: '10px',
                                  color: pin.direction === 'input' ? '#006064' : '#f57f17'
                                }}>
                                  {pin.direction === 'input' ? '←' : '→'} {pin.pinName}
                                  {pin.settingsSummary?.topic && ` (${pin.settingsSummary.topic})`}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </GradingSection>
                  );
                })
              ) : (
                <div className="grading-empty-state">
                  <div className="grading-empty-icon">🔴</div>
                  <p className="grading-empty-desc">
                    No flow node data captured. Submit for grading from the Node-RED dashboard
                    after building your flow.
                  </p>
                </div>
              )}

              {/* ── Wire connections ──────────────────────────────────────────── */}
              {nrFlowWires.length > 0 && (
                <GradingSection title={`Wire Connections (${nrFlowWires.length})`} icon="🔗" defaultOpen={false}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {nrFlowWires.map((w, i) => (
                      <div key={w.wireId || i} style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '5px 10px', background: '#f8fafc',
                        borderRadius: '5px', fontSize: '12px'
                      }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{w.fromNode}</span>
                        <span style={{ color: '#64748b', fontFamily: 'monospace' }}>[{w.fromPort}]</span>
                        <span style={{ color: '#94a3b8' }}>→</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{w.toNode}</span>
                        <span style={{ color: '#64748b', fontFamily: 'monospace' }}>[{w.toPort}]</span>
                      </div>
                    ))}
                  </div>
                </GradingSection>
              )}
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="grading-footer">
          {readOnly ? (
            /* ── Read-only (student view): score summary + Close only ── */
            <>
              <div className="grading-footer-result">
                {result ? (
                  <>
                    <span className="grading-footer-result-score"
                      style={{ color: scoreColor(result.score, totalMax) }}>
                      {result.score?.toFixed(2)}
                      <span className="grading-footer-result-max"> / {totalMax}</span>
                    </span>
                    {totalCount > 0 && (
                      <span className="grading-footer-result-reqs">
                        {passedCount}/{totalCount} requirements passed
                      </span>
                    )}
                  </>
                ) : (
                  <span className="grading-footer-result-reqs grading-footer-result-reqs--waiting">
                    ⏳ Grading result not yet available
                  </span>
                )}
              </div>
              <button className="grading-cancel-btn" onClick={onClose}>Close</button>
            </>
            ) : isPending ? (
              /* ── status=pending: grading in progress ── */
              <>
                <div className="grading-footer-result">
                  <span className="grading-footer-result-reqs grading-footer-result-reqs--waiting">
                    🤖 Prof-iX is grading your submission…
                  </span>
                </div>
                <button className="grading-cancel-btn" onClick={onClose}>Close</button>
              </>
            ) : isCompleted ? (
              /* ── status=completed: score ready, awaiting student confirmation ── */
              <>
                <div className="grading-footer-result">
                  <span className="grading-footer-result-score"
                    style={{ color: scoreColor(result.score, totalMax) }}>
                    {result.score?.toFixed(2)}
                    <span className="grading-footer-result-max"> / {totalMax}</span>
                  </span>
                  {totalCount > 0 && (
                    <span className="grading-footer-result-reqs">
                      {passedCount}/{totalCount} requirements passed
                    </span>
                  )}
                </div>
                <button className="grading-cancel-btn" onClick={onClose}>Cancel</button>
                <button className="grading-submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? '⏳ Submitting…' : '📨 Submit Grades'}
                </button>
              </>
            ) : isGraded ? (
              /* ── status=graded: locked ── */
              <>
                <div className="grading-footer-result">
                  <span className="grading-footer-result-score"
                    style={{ color: scoreColor(result.score, totalMax) }}>
                    {result.score?.toFixed(2)}
                    <span className="grading-footer-result-max"> / {totalMax}</span>
                  </span>
                  {totalCount > 0 && (
                    <span className="grading-footer-result-reqs">
                      {passedCount}/{totalCount} requirements passed
                    </span>
                  )}
                </div>
                <button className="grading-cancel-btn" onClick={onClose}>Close</button>
                <button className="grading-submit-btn grading-submit-btn--graded" disabled>
                  ✅ Grades Submitted
                </button>
              </>
            ) : (
            /* ── Submit mode, not yet submitted ── */
            <>
              <button className="grading-cancel-btn" onClick={onClose}>Close</button>
              <button className="grading-submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? '⏳ Submitting…' : '📤 Submit for Grading'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default SimulationReport;
